import { useState, useCallback, useMemo, FC, ReactNode } from 'react';
import { DeviceFamily, Placement } from '@amzn/fire-tv-campaign-automation-service';
import { FeatureFlag } from 'src/featureFlags';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { useSelection } from 'src/contexts/SelectionContext';

import { DevicePreviewNavigationContext } from './DevicePreviewNavigationContext';

interface DevicePreviewNavigationProviderProps {
  children: ReactNode;
}

/**
 * Provider component for the Device Preview Navigation context.
 * Manages the Test Campaign modal open/close state, computes device preview
 * availability, and provides stable callbacks via useMemo.
 */
export const DevicePreviewNavigationProvider: FC<DevicePreviewNavigationProviderProps> = ({
  children,
}) => {
  const [isTestCampaignModalOpen, setIsTestCampaignModalOpen] = useState(false);
  const { deviceFamily, placement } = useSelection();
  const isRdpEnabled = useFeatureFlag(FeatureFlag.RdpIntegration);

  const openTestCampaignModal = useCallback(() => setIsTestCampaignModalOpen(true), []);
  const closeTestCampaignModal = useCallback(() => setIsTestCampaignModalOpen(false), []);

  const isDevicePreviewAvailable =
    isRdpEnabled &&
    deviceFamily === DeviceFamily.FIRE_TV &&
    placement === Placement.FEATURE_ROTATOR;

  const value = useMemo(
    () => ({
      isTestCampaignModalOpen,
      openTestCampaignModal,
      closeTestCampaignModal,
      isDevicePreviewAvailable,
    }),
    [
      isTestCampaignModalOpen,
      openTestCampaignModal,
      closeTestCampaignModal,
      isDevicePreviewAvailable,
    ],
  );

  return (
    <DevicePreviewNavigationContext.Provider value={value}>
      {children}
    </DevicePreviewNavigationContext.Provider>
  );
};
